import { call, put, select } from 'redux-saga/effects'
import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import {
  selectActiveAddress,
  selectConfig,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { updateGasMeterAddressData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { saveMeterShared } from '@/containers/SubscriptionFormPageNew/Components/shared/MeterSaveSharedSagas'
import * as Sentry from '@sentry/react'

export const gasMeterModuleActions = {
  METER_MODULE_CHECK_DUPLICATED_PCE_V2: 'METER_MODULE_CHECK_DUPLICATED_PCE_V2',
  METER_MODULE_REQUEST_PCE_DATA_V2: 'METER_MODULE_REQUEST_PCE_DATA_V2',
  METER_MODULE_VALIDATE_PCE_METER_SYNTAX_V2: 'METER_MODULE_VALIDATE_PCE_METER_SYNTAX_V2',
  SAVE_GAS_METER_V2: 'SAVE_GAS_METER_V2',
  PCE_SEARCH_V2: 'PCE_SEARCH_V2',
}

export function* checkDuplicatedPceMeter({ meterId, energyType, duplicatedCallback }) {
  const { podValidateDuplicatedUrl } = yield select(selectExtraRoutesAuthData)
  const { contactId } = yield select(selectConfig)
  const fullUrl = `${podValidateDuplicatedUrl}?meterId=${meterId}&energyType=${energyType}`

  try {
    const data = yield call(callApi, {
      endpoint: fullUrl,
      asteriskReplacement: contactId,
      showErrorSnackbar: false,
    })

    yield call(duplicatedCallback, data)
  } catch (error) {
    yield call(duplicatedCallback, null)
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('checkDuplicatedMeterError'), { extra: { error } })
    }
  }
}

export function* requestPceMeterData({ body }) {
  const { podEligibilityUrl } = yield select(selectExtraRoutesAuthData)
  const activeAddress = yield select(selectActiveAddress)

  try {
    yield put(
      updateGasMeterAddressData({
        data: {
          pceEligibilityLoading: true,
          pceEligibilityData: {},
        },
        id: activeAddress,
      }),
    )
    yield call(callApi, {
      endpoint: podEligibilityUrl,
      showErrorSnackbar: false,
      payload: body,
      method: 'POST',
    })
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('requestMeterDataError'), { extra: { error } })
    }
  }
}

export function* validatePceMeterSyntax({ meterId, callback }) {
  const { podValidateUrl } = yield select(selectExtraRoutesAuthData)
  const fullUrl = `${podValidateUrl}?meterId=${meterId}&energyType=gas`
  try {
    const data = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
    })
    yield call(callback, data)
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('validateMeterSyntaxError'), { extra: { error } })
    }
  }
}

export function* saveGasMeter({ enqueue, meterData, addressId }) {
  return yield call(saveMeterShared, {
    enqueue,
    meterData,
    addressId,
    meterType: 'gas',
  })
}

export function* searchPce({ searchParams, onSuccess, onError }) {
  const { podSearchUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const params = new URLSearchParams({
      lookup_street_name: searchParams.streetName || '',
      zipcode: searchParams.zipcode || '',
      clientType: searchParams.contractType || 'part',
      pod_old_occupant: searchParams.occupant || '',
      matricule: searchParams.matricule || '',
      inseeCode: searchParams.inseeCode || '',
      apartment_complement: searchParams.complement || '',
      building: searchParams.building || '',
      locality: searchParams.locality || '',
      city: searchParams.city || '',
      serialNumber: searchParams.serialNumber || '',
      podType: 'PCE',
    })

    const fullUrl = `${podSearchUrl}?${params.toString()}`

    const response = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
    })

    if (response.response) {
      yield call(onSuccess, response.response)
      return
    }

    if (response.error_message) {
      yield call(onError, response.error_message)
      return
    }

    if (response.id) {
      yield call(onSuccess, null, response.id)
    }
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('searchPceError'), { extra: { error } })
    }
    yield call(onError, 'An error occurred while searching for PCE data')
  }
}
